import { FunctionComponent, JSX } from 'preact';
import { useCallback, useRef, useState } from 'preact/hooks';
import debounce from 'lodash/debounce';

import { usePlayer } from '../../hooks/PlayerProvider';
import { useJWPlayerEvent } from '../../hooks/useJWPlayerEvent';
import { useSliderControl } from '../../hooks/useSliderControl';
import { usePlayerMute } from '../../hooks/usePlayerMute';
import { useKeyboardHandler } from '../../hooks/useKeyboardHandler';
import { useVolume } from '../../hooks/useVolume';
import { useTracker } from '../../hooks/useTracker';

const volumeDiff = 5;

export const VolumeSlider: FunctionComponent = () => {
  const player = usePlayer();
  const { adjustVolume } = useVolume();
  const { isMuted } = usePlayerMute();
  const railRef = useRef<HTMLDivElement>(null!);
  const sliderRef = useRef<HTMLDivElement>(null!);
  const { trackButtonClick } = useTracker();
  const jw = player.getJWPlayer();

  const [volume, setVolume] = useState<number>(jw?.getVolume());

  const handleChange = useCallback(
    debounce((param: jwplayer.VolumeParam) => {
      setVolume(param.volume);
      trackButtonClick('volume');
    }, 100),
    [trackButtonClick]
  );

  useJWPlayerEvent('volume', handleChange);

  const handleSliderValueChange = useCallback(
    (value: number) => {
      setVolume(value);

      jw?.setVolume(value);
    },
    [jw]
  );

  useSliderControl({
    sliderRef,
    railRef,
    onSliderValueChange: handleSliderValueChange,
  });

  const handleWheel: JSX.WheelEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      e.preventDefault();

      setVolume((prevValue) => {
        const diff = (e.deltaY || -e.deltaX) * -0.1;

        const newValue = Math.min(100, Math.max(0, prevValue + diff));

        jw?.setVolume(newValue);

        return newValue;
      });
    },
    [jw]
  );

  const handleKeydown = useCallback(
    (e: KeyboardEvent) => {
      if (e.code === 'ArrowUp') {
        adjustVolume(volumeDiff);
      } else if (e.code === 'ArrowDown') {
        adjustVolume(-volumeDiff);
      }
    },
    [adjustVolume]
  );

  useKeyboardHandler(handleKeydown);

  const volumeLevel = isMuted ? 0 : volume;

  return (
    <div
      className="vgp-volume-slider"
      ref={sliderRef}
      role="slider"
      tabIndex={0}
      onWheel={handleWheel}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={volumeLevel}
    >
      <div className="vgp-volume-slider-rail" ref={railRef} />
      <div
        className="vgp-volume-level"
        style={{ right: `${100 - volumeLevel}%` }}
      />
      <div
        style={{
          right: `calc(${100 - volumeLevel}% - var(--volume-knob-size) / 2)`,
        }}
        className="vgp-volume-slider-knob"
      />
    </div>
  );
};
